import { Select, DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DateRangeType } from '../../../types/phlebotomist-dashboard';

const { RangePicker } = DatePicker;

type DateRangeOrder = 'rolling-first' | 'calendar-first';

interface IDateRangeSelectProps {
  value: DateRangeType;
  customRange?: { start: Date; end: Date };
  onChange: (
    dateRange: DateRangeType,
    customRange?: { start: Date; end: Date }
  ) => void;
  order?: DateRangeOrder;
  hideRolling?: boolean;
  hideCalendar?: boolean;
}

export const DateRangeSelect: FC<IDateRangeSelectProps> = ({
  value,
  customRange,
  onChange,
  order = 'rolling-first',
  hideRolling = false,
  hideCalendar = false,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');

  const options = useMemo(() => {
    const rollingGroup = {
      label: t('dateRange.rollingHeader', '— Rolling —'),
      options: [
        { value: 'day' as const, label: t('dateRange.day', 'Today') },
        {
          value: 'yesterday' as const,
          label: t('dateRange.yesterday', 'Yesterday'),
        },
        { value: 'week' as const, label: t('dateRange.week', 'Week') },
        {
          value: 'lastWeek' as const,
          label: t('dateRange.lastWeek', 'Last Week'),
        },
        { value: 'month' as const, label: t('dateRange.month', 'Month') },
        {
          value: 'lastMonth' as const,
          label: t('dateRange.lastMonth', 'Last Month'),
        },
        { value: 'quarter' as const, label: t('dateRange.quarter', 'Quarter') },
        {
          value: 'lastQuarter' as const,
          label: t('dateRange.lastQuarter', 'Last Quarter'),
        },
        { value: 'year' as const, label: t('dateRange.year', 'Year') },
        {
          value: 'lastYear' as const,
          label: t('dateRange.lastYear', 'Last Year'),
        },
      ],
    };

    const calendarGroup = {
      label: t('dateRange.calendarHeader', '— Calendar —'),
      options: [
        { value: 'dayCal' as const, label: t('dateRange.dayCal', 'Today') },
        {
          value: 'yesterdayCal' as const,
          label: t('dateRange.yesterdayCal', 'Yesterday'),
        },
        {
          value: 'weekCal' as const,
          label: t('dateRange.weekCal', 'This Week'),
        },
        {
          value: 'lastWeekCal' as const,
          label: t('dateRange.lastWeekCal', 'Last Week'),
        },
        {
          value: 'monthCal' as const,
          label: t('dateRange.monthCal', 'This Month'),
        },
        {
          value: 'lastMonthCal' as const,
          label: t('dateRange.lastMonthCal', 'Last Month'),
        },
        {
          value: 'quarterCal' as const,
          label: t('dateRange.quarterCal', 'This Quarter'),
        },
        {
          value: 'lastQuarterCal' as const,
          label: t('dateRange.lastQuarterCal', 'Last Quarter'),
        },
        {
          value: 'yearCal' as const,
          label: t('dateRange.yearCal', 'This Year'),
        },
        {
          value: 'pYearCal' as const,
          label: t('dateRange.pYearCal', 'Last Year'),
        },
      ],
    };

    const customOption = {
      value: 'custom' as const,
      label: t('dateRange.custom', 'Custom'),
    };

    const groups: (typeof rollingGroup | typeof customOption)[] = [];
    if (order === 'calendar-first') {
      if (!hideCalendar) groups.push(calendarGroup);
      if (!hideRolling) groups.push(rollingGroup);
    } else {
      if (!hideRolling) groups.push(rollingGroup);
      if (!hideCalendar) groups.push(calendarGroup);
    }
    groups.push(customOption);
    return groups;
  }, [t, order, hideRolling, hideCalendar]);

  const handleSelectChange = (newValue: DateRangeType) => {
    if (newValue !== 'custom') {
      onChange(newValue, undefined);
    } else {
      // When selecting custom, set a default range of last 7 days
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      onChange(newValue, { start, end });
    }
  };

  const handleRangeChange = (dates: [Moment | null, Moment | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      const startDate = dates[0].clone().startOf('day').toDate();
      const endDate = dates[1].clone().endOf('day').toDate();

      onChange('custom', {
        start: startDate,
        end: endDate,
      });
    }
  };

  const rangePickerValue = useMemo<[Moment, Moment] | undefined>(() => {
    if (customRange && customRange.start && customRange.end) {
      return [moment(customRange.start), moment(customRange.end)];
    }
    return undefined;
  }, [customRange?.start?.getTime(), customRange?.end?.getTime()]);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, width: '100%' }}>
      <Select
        value={value}
        onChange={handleSelectChange}
        options={options}
        style={{ flex: '1 1 140px', minWidth: 0 }}
        placeholder={t('dateRange.placeholder', 'Select range')}
      />
      {value === 'custom' && (
        <RangePicker
          value={rangePickerValue}
          onChange={handleRangeChange}
          allowClear={false}
          format="YYYY-MM-DD"
          style={{ flex: '1 1 200px', minWidth: 0 }}
        />
      )}
    </div>
  );
};

export default DateRangeSelect;
